
import { LoadingPriority, LoadingRequest } from './types';
import { NetworkDetector } from './NetworkDetector';

/**
 * Manages loading requests and their lifecycle
 */
export class RequestManager {
  private pendingRequests: Map<string, LoadingRequest> = new Map();
  private networkDetector: NetworkDetector;

  constructor(networkDetector: NetworkDetector) {
    this.networkDetector = networkDetector;
  }

  /**
   * Adds a new loading request
   */
  public addRequest(
    name: string,
    priority: LoadingPriority = 'normal',
    dependencies?: string[]
  ): string {
    // Create a unique ID for this request
    const id = `${name}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    
    // Check if a similar request is already pending (deduplication)
    const existingRequest = Array.from(this.pendingRequests.values()).find(
      (req) => req.name === name && Date.now() - req.startTime < 1000
    );
    
    if (existingRequest) {
      return existingRequest.id;
    }

    // Check if we should downgrade priority based on network conditions
    const { isLowBandwidth } = this.networkDetector.getNetworkStatus();
    if (isLowBandwidth && priority === 'normal') {
      priority = 'low';
    }

    const request: LoadingRequest = {
      id,
      name,
      priority,
      dependencies,
      startTime: Date.now(),
    };

    this.pendingRequests.set(id, request);
    return id;
  }

  /**
   * Completes a loading request
   */
  public completeRequest(id: string): void {
    if (this.pendingRequests.has(id)) {
      this.pendingRequests.delete(id);
      
      // Check if any requests were waiting on this one
      this.checkDependentRequests(id);
    }
  }

  /**
   * Gets all pending requests sorted by priority
   */
  public getPendingRequests(): LoadingRequest[] {
    return Array.from(this.pendingRequests.values())
      .sort((a, b) => {
        const priorityValues: Record<LoadingPriority, number> = {
          critical: 0,
          high: 1,
          normal: 2,
          low: 3,
        };
        return priorityValues[a.priority] - priorityValues[b.priority];
      });
  }

  /**
   * Clears all pending requests
   */
  public clearAllRequests(): void {
    this.pendingRequests.clear();
  }

  /**
   * Check for requests that depend on the completed request
   */
  private checkDependentRequests(completedId: string): void {
    // Find requests that had this request as a dependency
    Array.from(this.pendingRequests.values()).forEach(request => {
      if (request.dependencies && request.dependencies.includes(completedId)) {
        // Remove the completed request from dependencies
        request.dependencies = request.dependencies.filter(id => id !== completedId);
        
        // Update the request in the map
        this.pendingRequests.set(request.id, request);
      }
    });
  }

  /**
   * Group related requests into a batch
   */
  public createBatch(
    requests: Array<{ name: string; priority?: LoadingPriority }>,
    batchName: string
  ): Array<string> {
    const requestIds: string[] = [];
    
    // Create all requests first
    requests.forEach(({ name, priority = 'normal' }) => {
      const id = this.addRequest(`${batchName}:${name}`, priority);
      requestIds.push(id);
    });
    
    return requestIds;
  }

  /**
   * Complete a batch of requests
   */
  public completeBatch(requestIds: string[]): void {
    requestIds.forEach(id => this.completeRequest(id));
  }
}
